import { HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { ACTIONS } from "@enums/index.enum";
import { HttpHelperService } from "@services/http-helper.service";
import { map, Observable, take } from "rxjs";

import {
  DASHBOARD_DefaultParams,
  DASHBOARD_DefaultResponse,
  DASHBOARD_SearchParams,
} from "./dashboard.interface";

@Injectable()
export class DashboardService {
  constructor(private readonly httpHelperService: HttpHelperService) {}

  public getBranchList(userId) {
    return this.getDashboardReference(
      `/common/lov/type?userId=${userId}&listType=userBranchList`,
    );
  }

  public getAgentList(userId) {
    return this.getDashboardReference(`/common/agents?userId=${userId}`);
  }

  public getAgentListByBranch(userId, branchCode) {
    return this.getDashboardReference(
      `/common/agents?userId=${userId}&branchCode=${branchCode}`,
    );
  }

  public getReferredList() {
    return this.getDashboardReference(`/common/referred`);
  }

  public getMarketerManager(payload) {
    const { userId, pageSize, offset, searchValue, branchCode } = payload;
    let url = `/common/agent/market?userId=${userId}&pageSize=${pageSize}&offset=${offset}&searchValue=${searchValue}`;

    if (branchCode) {
      url += `&branchCode=${branchCode}`;
    }

    return this.getDashboardReference(url);
  }

  public getLOV(productCode: string, userId: string) {
    return this.getDashboardReference(
      `/common/lov?lob&mainProdType=${productCode.toUpperCase()}&userId=${userId}`,
    );
  }

  public getUnifiedLOV(userId: string) {
    return this.getDashboardReference(`/common/unified/lov?userId=${userId}`);
  }

  public getUnifiedLOVInSummaryDashboard(userId: string) {
    return this.getDashboardReference(
      `/common/unified/lov?userId=${userId}&action=SUMMARY`,
    );
  }

  // Keep dashboard table routing inside the service. Components and NGXS state
  // should stay agnostic to whether data comes from the real backend or the
  // local json-server.
  public fetchDefaultList(
    body: DASHBOARD_DefaultParams,
  ): Observable<DASHBOARD_DefaultResponse> {
    const { userId, productType } = body;

    let params = new HttpParams()
      .set("userId", userId)
      .set("productType", productType);
    params = body.summaryViewType
      ? params.set("summaryViewType", body.summaryViewType)
      : params;

    return this.httpHelperService.getWithOptions("/cn/summary", params);
    // return of(defaultList);
  }

  public searchList(
    body: DASHBOARD_SearchParams,
  ): Observable<DASHBOARD_DefaultResponse> {
    return this.httpHelperService.postWithOptions("/cn/summary/search", body);
  }

  public generateEschedule(body) {
    return this.httpHelperService.post("/cn/save/covernote", body);
  }

  public downloadProposal(payload) {
    // http://localhost:8043/alpha-modular-svc/print/proposal?contractId=151362&operator=vidya_afc
    const { popUp } = payload;

    if (popUp) {
      popUp.location.href = this.httpHelperService.getURLString(
        `/print/proposal?contractId=${payload.contractId}&operator=${payload.operator}&productType=${payload.productType}`,
      );
      setTimeout(() => popUp.close(), 5000);
    } else {
      window.open(
        this.httpHelperService.getURLString(
          `/print/proposal?contractId=${payload.contractId}&operator=${payload.operator}&productType=${payload.productType}`,
        ),
      );
    }
  }

  public downloadESchedule(payload) {
    //add triggerEmail params--> set to true if auto download
    window.open(
      this.httpHelperService.getURLString(
        `/print/eSchedule?contractId=${payload.contractId}&operator=${payload.operator}&triggerEmail=${payload.triggerEmail}&productType=${payload.productType}&&einvprint=${payload.einvprint}`,
      ),
    );
    // return this.httpHelperService.get(`/print/eSchedule?contractId=${payload.contractId}&operator=${payload.operator}`);
  }

  public getDraftDetail(payload) {
    return this.httpHelperService.get(
      `/cn/summary/search/detail?contractId=${payload.contractId}&operator=${payload.operator}`,
    );
  }

  public getDirectUrlFromSubMenu(body): Observable<any> {
    return this.httpHelperService.getDirectUrl(body);
  }

  public getMenuAccess(userId) {
    return this.httpHelperService.get(`/user/menuaccess?userId=${userId}`);
  }

  public getEnquiryList(body) {
    return this.httpHelperService.post("/policy/search", body);
  }

  public redirectToSatMotor(payload): Observable<any> {
    const params = new URLSearchParams({
      userId: payload.operator,
      contractId: payload.contractId,
      productType: payload.productType,
      productCode: payload.productCode,
      action: payload.action,
    });
    const path = `/ud/switching?${params.toString()}`;
    return this.httpHelperService.getWithOptions(path, new HttpParams(), {
      baseUrl: this.httpHelperService.resolveSessionBaseUrl(),
    });
  }

  public getRedirectPage(body): Observable<any> {
    const { action, operator } = body;
    let url =
      action === ACTIONS.PROFILE
        ? `/ud/switching?switch&userId=${operator}`
        : `/ud/switching?userId=${operator}`;

    url += this.appendParam("agentCode", body.agentCode);
    url += this.appendParam("productType", body.productType);
    url += this.appendParam("productCode", body.productCode);
    url += this.appendParam("action", body.action);
    url += this.appendParam("documentNo", body.documentNo, true);
    url += this.appendParam("contractId", body.contractId);
    url += this.appendParam("cnoteType", body.cnoteType);
    url += this.appendParam("idType", body.idType);
    url += this.appendParam("idValue", body.idValue);
    url += this.appendParam("clientType", body.clientType);
    url += this.appendParam("enquiryProduct", body.enquiryProduct);
    url += this.appendParam("searchCriteria", body.searchCriteria);
    url += this.appendParam("searchValue", body.searchValue, true);
    url += this.appendParam("customerType", body.customerType);
    url += this.appendParam("token", body.token);
    url += this.appendParam("notificationId", body.notificationId);
    url += this.appendParam("policyNo", body.policyNo, true);
    url += this.appendParam("renewNow", body.renewNow);
    url += this.appendParam("reQuote", body.reQuote);
    url += this.appendParam("reProposal", body.reProposal);

    return this.httpHelperService.getWithOptions(url, new HttpParams(), {
      baseUrl: this.httpHelperService.resolveSessionBaseUrl(),
    });
  }

  private appendParam(key: string, value: string, encode = false): string {
    if (!value) {
      return "";
    }
    return `&${key}=${encode ? encodeURIComponent(value) : value}`;
  }

  public getProductAccess(payload): Observable<any> {
    return this.httpHelperService.get(
      `/common/userProductAccess?product=${payload.productType}&userId=${payload.userId}`,
    );
  }

  public getProductTypeByProductCode(productCode): Observable<string> {
    return this.httpHelperService
      .getWithOptions(
        `/common/getProductTypeByProductCode?productCode=${productCode}`,
        new HttpParams(),
      )
      .pipe(
        take(1),
        map((res) => res.data.productType),
      );
  }

  public getDocumentLov(body) {
    return this.httpHelperService.post(`/common/lov/documents`, body);
  }

  public getDocuments(body) {
    return this.httpHelperService.get(
      `/cn/documents?key=${body.key}&value=${body.value}`,
    );
  }

  public getRequoteDocuments(body) {
    return this.httpHelperService.post(`/cn/documents/requote`, body);
  }

  public downloadDocument(docId) {
    return this.httpHelperService.downloadFile(
      `/cn/documents/download?docId=${docId}`,
    );
  }

  public downloadMotorApprovalReport(body) {
    const {
      branchCode,
      agentCode,
      startDate,
      cnoteStatus,
      endDate,
      operator,
      referCode,
      productType,
      cnoteNo,
      cnoteType,
      marketerManager,
      vehNo,
    } = body;
    const params = new HttpParams()
      .set("userId", operator)
      .set("branchCode", branchCode)
      .set("agentCode", agentCode)
      .set("cnoteStatus", cnoteStatus)
      .set("startDate", startDate)
      .set("endDate", endDate)
      .set("productType", productType)
      .set("cnoteType", cnoteType)
      .set("vehNo", vehNo)
      .set("cnoteNo", cnoteNo)
      .set("marketerManager", marketerManager)
      .set("isSatMotorInd", true)
      .set("referCode", referCode);

    return window.open(
      this.httpHelperService.getURLString(
        `/report/motorApproval/download?${params}`,
      ),
    );
    // return this.httpHelperService.getURL(`/report/production/download?${params}`);
  }

  public getActiveAgentsList(payload) {
    const { userId, pageSize, offset, searchValue, branchCode } = payload;
    let url = `/common/agent/searchlist?userId=${userId}&pageSize=${pageSize}&offset=${offset}&searchValue=${searchValue}`;

    if (branchCode) {
      url += `&branchCode=${branchCode}`;
    }

    return this.getDashboardReference(url);
  }

  // Dashboard table/reference reads delegate base URL selection to
  // HttpHelperService.resolveBackendBaseUrl(). No feature-level mock-routing
  // branches are needed here.
  private getDashboardReference(path: string): Observable<any> {
    return this.httpHelperService.getWithOptions(path, new HttpParams());
  }
}
